import React, { useRef } from "react";
import { Button, Typography, Upload, theme } from "antd";

const { Text } = Typography;

/**
 * Donde se cargan las imágenes del producto (xcons-docs#490): un recuadro punteado con
 * **Elegir archivos** o **carpetas**, "desde tu computadora, o arrastralos acá".
 *
 * El recuadro no abre el selector al clickearlo —lo abren los dos botones—, pero sí recibe
 * lo que se suelta encima, también una carpeta entera: por eso lleva `directory`. De una
 * carpeta se suben sólo las imágenes.
 *
 * Los botones abren dos `input` propios y no un `Upload` cada uno: un `Upload` adentro del
 * recuadro hereda su relleno y los separaba del texto.
 */
export const ZonaDeImagenes: React.FC<{
  /** Sube un archivo. Devuelve `false` para que antd no lo suba por su cuenta. */
  onArchivo: (file: File) => Promise<boolean> | boolean;
  disabled?: boolean;
}> = ({ onArchivo, disabled }) => {
  const { token } = theme.useToken();
  const archivos = useRef<HTMLInputElement>(null);
  const carpetas = useRef<HTMLInputElement>(null);

  const boton: React.CSSProperties = {
    background: token.colorPrimaryBg,
    color: token.colorPrimary,
    borderColor: token.colorPrimaryBorder,
    fontWeight: 600,
    paddingInline: 12,
  };

  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target.files ?? [])
      .filter((f) => f.type.startsWith("image/"))
      .forEach((f) => void onArchivo(f));
    // Vacío, para que elegir el mismo archivo otra vez vuelva a dispararlo.
    e.target.value = "";
  };

  return (
    <Upload.Dragger
      accept="image/*"
      multiple
      directory
      openFileDialogOnClick={false}
      showUploadList={false}
      beforeUpload={onArchivo}
      disabled={disabled}
      style={{ cursor: "default" }}
    >
      {/* Más alto que el default de antd (xcons-docs#490): el recuadro es donde se suelta. */}
      <Text type="secondary" style={{ display: "block", padding: "20px 16px" }}>
        <Button
          size="small"
          style={boton}
          disabled={disabled}
          onClick={() => archivos.current?.click()}
        >
          Elegir archivos
        </Button>{" "}
        o{" "}
        <Button
          size="small"
          style={boton}
          disabled={disabled}
          onClick={() => carpetas.current?.click()}
        >
          carpetas
        </Button>{" "}
        desde tu computadora, o arrastralos acá
      </Text>
      <input
        ref={archivos}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={alElegir}
      />
      <input
        ref={carpetas}
        type="file"
        // `webkitdirectory` no está en los tipos de React: se pasa como atributo suelto.
        {...{ webkitdirectory: "", directory: "" }}
        multiple
        hidden
        onChange={alElegir}
      />
    </Upload.Dragger>
  );
};
